/**
 * Dashboard home — bento "today" cockpit.
 *
 * Asymmetric grid: hero AI summary card (with animated gradient border) spans
 * wide; safety score sits as a tall companion; stats row uses animated number
 * tickers; map preview, risk alerts, pickup, device health and monthly
 * report fill the remaining tiles at varied sizes.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Activity, MapPin, ShieldCheck, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NotificationsBell } from '@/dashboard/NotificationsBell';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Toaster } from '@/components/ui/sonner';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { cn } from '@/lib/utils';
import { usersApi, type Child } from '@/api/users';
import { locationsApi } from '@/api/locations';
import { KidzioMap } from '@/components/map/KidzioMap';
import type { KidzioMapMarker, KidzioGeofence } from '@/components/map/types';
import { AiSummaryCard } from '@/dashboard/AiSummaryCard';
import { PickupCard } from '@/dashboard/PickupCard';
import { RiskAlertsCard } from '@/dashboard/RiskAlertsCard';
import { DeviceHealthCard } from '@/dashboard/DeviceHealthCard';
import { SafetyScoreCard } from '@/dashboard/SafetyScoreCard';
import { MonthlyReportCard } from '@/dashboard/MonthlyReportCard';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.kidzio.io';

function greetingKey(): string {
  const h = new Date().getHours();
  if (h < 5) return 'greeting.night';
  if (h < 12) return 'greeting.morning';
  if (h < 18) return 'greeting.afternoon';
  return 'greeting.evening';
}

type Tint = 'blue' | 'coral' | 'green' | 'amber';
const STAT_TINT: Record<Tint, { chip: string; halo: string }> = {
  blue: { chip: 'bg-[#4f6bff]/10 text-[#4f6bff]', halo: 'from-[#4f6bff]/20 to-transparent' },
  coral: { chip: 'bg-[#ff7a59]/12 text-[#ff7a59]', halo: 'from-[#ff7a59]/22 to-transparent' },
  green: { chip: 'bg-[#22c55e]/12 text-[#16a34a]', halo: 'from-[#22c55e]/22 to-transparent' },
  amber: { chip: 'bg-[#ffc542]/20 text-[#d39400]', halo: 'from-[#ffc542]/28 to-transparent' },
};

function StatCard({
  icon: Icon,
  label,
  value,
  tint,
  number,
  to,
}: {
  icon: typeof MapPin;
  label: string;
  value?: string;
  tint: Tint;
  number?: number;
  to?: string;
}) {
  const t = STAT_TINT[tint];
  const inner = (
    <Card className="group relative h-full overflow-hidden">
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute -right-10 -top-10 size-32 rounded-full bg-gradient-to-br opacity-60 blur-2xl transition-opacity duration-300 group-hover:opacity-100',
          t.halo,
        )}
      />
      <CardContent className="relative flex items-center gap-3 p-4">
        <span
          className={cn(
            'grid size-11 place-items-center rounded-2xl transition-transform group-hover:scale-105',
            t.chip,
          )}
        >
          <Icon className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-medium text-[color:var(--kz-color-ink-secondary)]">{label}</p>
          {typeof number === 'number' ? (
            <AnimatedNumber
              value={number}
              className="text-2xl font-extrabold tabular-nums tracking-tight text-[color:var(--kz-color-ink-primary)]"
            />
          ) : (
            <p className="truncate text-xl font-bold capitalize tracking-tight text-[color:var(--kz-color-ink-primary)]">
              {value}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} className="block h-full">{inner}</Link> : inner;
}

export function App() {
  const { t } = useTranslation();
  const family = useQuery({
    queryKey: ['family-me'],
    queryFn: usersApi.myFamily,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => usersApi.listChildren(),
    retry: false,
  });
  // Same react-query key the Map page uses — fetch is shared / deduped
  // across both surfaces. Drives the real-position preview tile below.
  const familyCurrent = useQuery({
    queryKey: ['family-current'],
    queryFn: () => locationsApi.familyCurrent(),
    retry: false,
    refetchInterval: 60_000,
  });

  const greeting = t(greetingKey());
  const familyName = family.data?.name ?? 'family';
  const initial = (family.data?.name?.trim()[0] ?? 'K').toUpperCase();
  const kidsCount = children.data?.length ?? 0;
  const plan = family.data?.plan ?? 'free';

  return (
    <main className="container mx-auto max-w-7xl px-4 py-8 sm:px-6 md:py-10">
      <Toaster position="top-right" />

      <motion.section
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="flex flex-wrap items-center justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <Avatar className="size-14 ring-2 ring-white shadow-md">
            <AvatarFallback className="bg-gradient-to-br from-[#4f6bff] to-[#ff7a59] text-lg font-bold text-white">
              {initial}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-3xl font-extrabold leading-tight tracking-tight md:text-4xl">
              {greeting} <span className="inline-block">👋</span>
            </h1>
            <p className="text-sm text-[color:var(--kz-color-ink-secondary)]">
              {kidsCount > 0
                ? t('greeting.kids_count', { count: kidsCount, family: familyName })
                : t('greeting.welcome', { family: familyName })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationsBell />
        </div>
      </motion.section>

      {kidsCount === 0 && !children.isPending && (
        <Card className="mt-6 border-[color:var(--kz-color-brand-primary)]/30 bg-[color:var(--kz-color-brand-primary)]/5">
          <CardHeader>
            <CardTitle>{t('home.setup_title')}</CardTitle>
            <CardDescription>{t('home.setup_description')}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button asChild>
              <Link to="/children">{t('home.setup_cta')}</Link>
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Attention strip — only renders when a device needs the parent's eye. */}
      <div className="mt-6">
        <DeviceHealthCard />
      </div>

      {/* Hero row: AI summary + 2x2 stats */}
      <section className="mt-8 grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-12">
        <div className="kz-gradient-border lg:col-span-8">
          <AiSummaryCard />
        </div>
        <div className="grid grid-cols-2 gap-4 md:gap-5 lg:col-span-4">
          <StatCard icon={Users} label={t('stats.children')} number={kidsCount} tint="coral" to="/children" />
          <StatCard icon={ShieldCheck} label={t('stats.safety')} value={t('stats.healthy')} tint="green" to="/safety" />
          <StatCard icon={MapPin} label={t('stats.live_map')} value={t('stats.ready')} tint="blue" to="/map" />
          <StatCard icon={Activity} label={t('stats.plan')} value={plan} tint="amber" to="/settings" />
        </div>
      </section>

      {/* Lower bento — two independent columns so short cards don't leave
          a vacuum next to taller siblings. Left is 7/12, right is 5/12. */}
      <section className="mt-6 grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-12">
        <div className="flex flex-col gap-4 md:gap-5 lg:col-span-7">
          <Card>
            <CardHeader>
              <CardTitle>{t('home.live_map_title')}</CardTitle>
              <CardDescription>{t('home.live_map_description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <LiveMapPreview
                kids={children.data ?? []}
                positions={familyCurrent.data?.positions ?? []}
                geofences={familyCurrent.data?.geofences ?? []}
              />
            </CardContent>
            <CardFooter>
              <Button asChild variant="outline">
                <Link to="/map">{t('home.open_map')}</Link>
              </Button>
            </CardFooter>
          </Card>
          <RiskAlertsCard />
        </div>

        <div className="flex flex-col gap-4 md:gap-5 lg:col-span-5">
          <SafetyScoreCard />
          <PickupCard />
          <MonthlyReportCard />
        </div>
      </section>
    </main>
  );
}

/**
 * Mini-map preview on the dashboard.
 *
 * Renders the real MapLibre map (read-only, no toolbar) with each child's
 * latest position and saved geofences. Wrapped in `pointer-events-none` so
 * the dashboard can't accidentally drag/zoom; an absolute `<Link>` overlay
 * sends the click to /map. Falls back to a decorative SVG diorama when
 * there's no live position data yet (e.g. first paint, no children paired).
 */
type KidTint = { pin: string; routeColor: string };
const KID_TINTS: readonly [KidTint, KidTint] = [
  { pin: '#4f6bff', routeColor: '#4f6bff' },
  { pin: '#ff7a59', routeColor: '#ff7a59' },
] as const;

interface MiniPosition {
  child_id: string;
  lat: number;
  lng: number;
  color?: string | null;
  freshness_seconds?: number;
  battery_pct?: number | null;
}
interface MiniGeofence {
  id: string;
  name: string;
  center_lat: number;
  center_lng: number;
  radius_m: number;
  icon?: string | null;
}

function LiveMapPreview({
  kids,
  positions,
  geofences,
}: {
  kids: Child[];
  positions: MiniPosition[];
  geofences: MiniGeofence[];
}) {
  const { t } = useTranslation();
  const visibleKids = kids.slice(0, 2);
  const labelFor = (c: Child) => (c.nickname?.trim() || c.name?.trim() || 'Kid').slice(0, 1).toUpperCase();

  // Static positions of the two child pins on the 320x160 canvas. Picked
  // to land on different "blocks" so the routes have visible curvature.
  const PIN_POS: readonly [{ x: number; y: number }, { x: number; y: number }] = [
    { x: 124, y: 96 },
    { x: 196, y: 50 },
  ] as const;
  const HOME = { x: 58, y: 48 };

  // Real-map mode: when we have at least one fresh position, render the
  // actual MapLibre map. Otherwise we keep the decorative SVG diorama as a
  // first-paint placeholder. Each child's freshest position wins so older
  // pings don't show them stale.
  const childById = new Map(kids.map((c) => [c.id, c]));
  const byChild = new Map<string, MiniPosition>();
  for (const p of positions) {
    const existing = byChild.get(p.child_id);
    if (!existing || (p.freshness_seconds ?? 0) < (existing.freshness_seconds ?? 0)) {
      byChild.set(p.child_id, p);
    }
  }
  const mapMarkers: KidzioMapMarker[] = Array.from(byChild.values()).map((p) => {
    const info = childById.get(p.child_id);
    const name = info?.nickname?.trim() || info?.name?.trim() || '?';
    return {
      id: p.child_id,
      lat: p.lat,
      lng: p.lng,
      color: info?.color ?? p.color ?? '#4f6bff',
      label: name,
      avatarUrl: info?.avatar_url ? `${API_BASE}${info.avatar_url}` : undefined,
      battery_pct: p.battery_pct ?? undefined,
      freshness_seconds: p.freshness_seconds,
    };
  });
  const mapGeofences: KidzioGeofence[] = geofences.map((g) => ({
    id: g.id,
    name: g.name,
    center_lat: g.center_lat,
    center_lng: g.center_lng,
    radius_m: g.radius_m,
    icon: g.icon ?? null,
  }));

  if (mapMarkers.length > 0) {
    return (
      <div className="relative aspect-[16/8] w-full overflow-hidden rounded-xl shadow-inner">
        <div className="absolute inset-0 pointer-events-none">
          <KidzioMap markers={mapMarkers} geofences={mapGeofences} basemap="voyager" />
        </div>
        {/* Clickable overlay — sends the dashboard click to the full map.
            Covers the entire preview so the user doesn't have to aim. */}
        <Link
          to="/map"
          aria-label="Open the family map"
          className="absolute inset-0 z-10 block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4f6bff]/60"
        />
        <div className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/85 px-2 py-0.5 text-[10px] font-medium text-[color:var(--kz-color-ink-secondary)] shadow-sm backdrop-blur">
          <span className="size-1.5 rounded-full bg-green-500" />
          {t('home.live_chip')}
        </div>
      </div>
    );
  }

  return (
    <div className="relative aspect-[16/8] w-full overflow-hidden rounded-xl bg-gradient-to-br from-[#eaf0fb] via-[#f1f5ff] to-[#e6efe9] shadow-inner">
      <svg
        viewBox="0 0 320 160"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 h-full w-full"
        role="img"
        aria-label="Live family map preview"
      >
        <defs>
          <linearGradient id="kzPinBlue" x1="0" y1="0" x2="0" y2="32" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#6a86ff" />
            <stop offset="1" stopColor="#4f6bff" />
          </linearGradient>
          <linearGradient id="kzPinCoral" x1="0" y1="0" x2="0" y2="32" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#ff9a7c" />
            <stop offset="1" stopColor="#ff7a59" />
          </linearGradient>
          <radialGradient id="kzGeofenceFill" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Water body bottom-right — soft asymmetric blob */}
        <path d="M210 160 C 240 130, 290 140, 320 120 L 320 160 Z" fill="#bcd6f4" opacity="0.55" />

        {/* Parks (rounded green blobs) */}
        <rect x="14" y="18" width="76" height="44" rx="14" fill="#cfe7d2" />
        <rect x="222" y="98" width="84" height="40" rx="14" fill="#cfe7d2" />

        {/* Building blocks */}
        <rect x="108" y="14" width="58" height="36" rx="6" fill="#dfe5f2" />
        <rect x="174" y="14" width="42" height="36" rx="6" fill="#e4eaf6" />
        <rect x="108" y="92" width="58" height="46" rx="6" fill="#e0e6f3" />
        <rect x="14" y="92" width="76" height="46" rx="6" fill="#e4eaf6" />
        <rect x="174" y="92" width="42" height="46" rx="6" fill="#dfe5f2" />

        {/* Roads */}
        <g stroke="#ffffff" strokeWidth="10" fill="none" strokeLinecap="round">
          <path d="M-6 72H326" />
          <path d="M100 -6V166" />
          <path d="M170 -6V166" />
        </g>
        {/* Lane markings */}
        <g stroke="#cdd6e4" strokeWidth="1" strokeDasharray="3 6" fill="none">
          <path d="M-6 72H326" />
        </g>

        {/* Geofence around home */}
        <circle cx={HOME.x} cy={HOME.y} r="30" fill="url(#kzGeofenceFill)" />
        <circle cx={HOME.x} cy={HOME.y} r="30" fill="none" stroke="#22c55e" strokeOpacity="0.45" strokeWidth="1.25" strokeDasharray="2 4" />

        {/* Route trails from home → each child */}
        {visibleKids.length > 0 && (
          <path
            d={`M ${HOME.x} ${HOME.y} C 80 70, 100 90, ${PIN_POS[0].x} ${PIN_POS[0].y}`}
            stroke={KID_TINTS[0].routeColor}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="1 6"
            fill="none"
          />
        )}
        {visibleKids.length > 1 && (
          <path
            d={`M ${HOME.x} ${HOME.y} C 110 40, 160 56, ${PIN_POS[1].x} ${PIN_POS[1].y}`}
            stroke={KID_TINTS[1].routeColor}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="1 6"
            fill="none"
          />
        )}

        {/* Home marker (static green dot) */}
        <circle cx={HOME.x} cy={HOME.y} r="7" fill="#22c55e" stroke="#ffffff" strokeWidth="3" />

        {/* Child pins */}
        {visibleKids.map((c, i) => {
          const pos = i === 0 ? PIN_POS[0] : PIN_POS[1];
          const tint = i === 0 ? KID_TINTS[0] : KID_TINTS[1];
          const gradId = i === 0 ? 'kzPinBlue' : 'kzPinCoral';
          return (
            <g key={c.id} transform={`translate(${pos.x - 12} ${pos.y - 28})`}>
              <ellipse cx="12" cy="32" rx="6" ry="1.5" fill="#000" opacity="0.18" />
              <path
                d="M12 0C5.4 0 0 5.4 0 12C0 21 12 32 12 32C12 32 24 21 24 12C24 5.4 18.6 0 12 0Z"
                fill={`url(#${gradId})`}
              />
              <circle cx="12" cy="12" r="6.5" fill="#ffffff" />
              <text
                x="12"
                y="14.6"
                fontSize="8"
                fontWeight="800"
                textAnchor="middle"
                fill={tint.pin}
                fontFamily="ui-sans-serif, system-ui, sans-serif"
              >
                {labelFor(c)}
              </text>
            </g>
          );
        })}
      </svg>

      {/* A single, quiet live indicator — no count chip, no per-child pills,
          no scale label. The map's pins already speak for themselves. */}
      <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-medium text-[color:var(--kz-color-ink-secondary)]">
        <span className="size-1.5 rounded-full bg-green-500" />
        {t('home.live_chip')}
      </div>
    </div>
  );
}
